import React, { useState, useEffect } from 'react';
import { DownOutlined } from '@ant-design/icons';
import {
  Drawer,
  Table,
  Empty,
  Menu,
  Dropdown,
  Button,
  Spin,
  Tag,
  message,
  Select,
  Skeleton,
} from 'antd';
import { DrawerProps } from 'antd/lib/drawer';
import {
  FETCH_SERVICE_DRIVERS_INFO,
  FETCH_ALL_DRIVERS_FROM_DRIVING_COMPANY,
} from '../../queries';
import {
  sendEmail,
  constructEmail,
  openEmailClient,
} from '../../../../utils/email';
import {
  CONNECT_DRIVER_TO_SERVICE,
  DISCONNECT_DRIVER_FROM_SERVICE,
} from '../../../drivers/mutations';
import { MAKE_PRINCIPAL_DRIVER, MAKE_SECONDARY_DRIVER } from '../../mutations';
import { useQuery } from '@apollo/client';
import { getClient } from '../../../../apollo';
import {
  ServiceQuery,
  useServiceQuery,
} from '../../../../graphql/datamodel.gen';

const { Option } = Select;

const addDriverToService = async (email: string, volcanoServiceId: string) => {
  const response = await getClient().mutate({
    mutation: CONNECT_DRIVER_TO_SERVICE,
    variables: {
      email: email.toLowerCase(),
      volcanoServiceId,
    },
  });

  return response.data;
};

const removeDriverFromService = async (
  email: string,
  volcanoServiceId: string,
) => {
  const response = await getClient().mutate({
    mutation: DISCONNECT_DRIVER_FROM_SERVICE,
    variables: {
      email: email.toLowerCase(),
      volcanoServiceId,
    },
  });

  return response.data;
};

interface IDriver {
  id: string;
  email: string;
  phone?: string;
  alternatePhone?: string;
  name: string;
  volcanoDriverId?: string;
  inPrisma: boolean;
  inVolcano: boolean;
}

interface IRow extends IDriver {
  key: string;
}

interface IDriverManagementProps {
  refetch?: any;
  serviceId: string;
  volcanoServiceId: string;
  drivers?: IDriver[];
  allDrivers?: IDriver[];
  driver?: IDriver;
}

interface IDriverManagementView {
  refetch?: any;
  serviceId: string;
  driver?: ServiceQuery['service']['driver'];
  alternateDriver?: ServiceQuery['service']['alternateDriver'];
}

interface IDriverManagementState {
  dataSource: any[];
  count: number;
}

interface DriverManagementContainerProps {
  serviceId: string;
}

const DriverManagementContainer = (props: DriverManagementContainerProps) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks

  const {
    loading: loadingServiceDrivers,
    error: errorServiceDrivers,
    data: dataServiceDrivers,
    refetch: refetchServiceDrivers,
  } = useServiceQuery({
    client: getClient(),
    variables: {
      id: props.serviceId,
    },
    fetchPolicy: 'network-only',
  });

  if (errorServiceDrivers) {
    console.error(errorServiceDrivers);
  }

  if (loadingServiceDrivers) {
    return <Skeleton active={true} loading={true} />;
  }

  const driver = dataServiceDrivers?.service?.driver;
  const alternateDriver = dataServiceDrivers?.service?.alternateDriver;

  return (
    <DriverManagementView
      refetch={refetchServiceDrivers}
      serviceId={props.serviceId}
      driver={driver}
      alternateDriver={alternateDriver}
    />
  );
};

class DriverManagementView extends React.Component<
  IDriverManagementView,
  IDriverManagementState
> {
  private columns: any[];

  constructor(props: IDriverManagementView) {
    super(props);
    this.columns = [
      {
        title: 'Principal',
        dataIndex: 'mainDriver',
        width: 75,
        render: (text: any, record: IRow) => {
          if (this.props.driver) {
            if (record.id === this.props.driver.id)
              return (
                <Tag color={'green'} key={'yes'}>
                  {'SI'}
                </Tag>
              );
          }

          return (
            <Tag color={'volcano'} key={'yes'}>
              {'NO'}
            </Tag>
          );
        },
      },
      {
        title: 'ID',
        dataIndex: 'id',
        ellipsis: true,
        width: 100,
        render: (_: any, record: IRow) => {
          return record.volcanoDriverId || record.id;
        },
      },
      {
        title: 'Email',
        dataIndex: 'email',
      },
      {
        title: 'Teléfono',
        dataIndex: 'phone',
        render: (text: any, record: IRow) => {
          return record.alternatePhone || record.phone || '';
        },
      },
      {
        title: 'Nombre',
        dataIndex: 'name',
      },
      {
        title: 'Operaciones',
        dataIndex: 'operation',
        width: 125,
        render: (text: any, record: IRow) => {
          const menus = (
            <Menu>
              <Menu.Item onClick={() => openEmailClient(record.email)}>
                Enviar email
              </Menu.Item>
              <Menu.Item disabled={true}>Llamar</Menu.Item>
              <Menu.Item disabled={true}>Enviar Whatsapp</Menu.Item>
            </Menu>
          );

          return this.state.dataSource.length >= 1 ? (
            <Dropdown overlay={menus}>
              <Button size="small">
                Acciones <DownOutlined translate={undefined} />
              </Button>
            </Dropdown>
          ) : null;
        },
      },
    ];

    const drivers = [];

    if (this.props.driver) {
      drivers.push({
        ...this.props.driver,
        key: this.props.driver.email,
      });
    }

    if (this.props.alternateDriver) {
      drivers.push({
        ...this.props.alternateDriver,
        key: this.props.alternateDriver.email,
      });
    }

    const count = drivers.length;

    this.state = {
      dataSource: drivers,
      count,
    };
  }

  render() {
    const { dataSource } = this.state;
    return (
      <div>
        <Table
          locale={{
            emptyText: (
              <Empty
                description="No hay conductores asignados todavía"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size={'small'}
          bordered
          dataSource={dataSource}
          columns={this.columns}
          pagination={false}
        />
      </div>
    );
  }
}

interface IDriverManagementDrawerProps
  extends DrawerProps,
    IDriverManagementProps {
  visible: boolean;
  onClose: () => void;
}

export default class DriverManagementDrawer extends React.Component<IDriverManagementDrawerProps> {
  render() {
    return (
      <Drawer
        {...this.props}
        destroyOnClose={true}
        title={`Ver conductores del servicio ${this.props.volcanoServiceId}`}
        visible={this.props.visible}
        onClose={this.props.onClose}
      >
        <DriverManagementContainer serviceId={this.props.serviceId} />
      </Drawer>
    );
  }
}
